/* global Modernizr */

import { Controller } from 'stimulus';
import qs from 'qs';
import jump from 'jump.js';
import formSerialize from 'form-serialize';
import serializeFormToObject from '@/helpers/serializeFormToObject';
import {
  deleteFilterForButton,
  getDefaultForInput,
  getInputForKeyAndValue,
  getLabelTextForInput,
  renderSelectedFilter,
  clearFilters,
} from '@/helpers/filterHelpers';
import hasHistorySupport from '@/helpers/hasHistorySupport';
import debounce from '@/helpers/debounce';
import { format } from 'date-fns';

class FilterController extends Controller {
  connect() {
    if (this.hasSectionTarget) {
      this.isFixed = false;
      this.headerHeight = document.querySelector('.header').clientHeight;
      this.sectionTop = this.sectionTarget.offsetTop - this.headerHeight;
    }

    this.filterDebounced = debounce(this.filter.bind(this), 10);

    $(this.element).on('click', '.pagination a', (e) => {
      e.preventDefault();

      const pageNumber = parseInt(e.currentTarget.dataset.page, 10);

      if (typeof pageNumber === 'undefined') return;

      this.setPageNumber(pageNumber);
      this.scrollToResult();
      this.onFilterChange();
    });

    this.onFilterChange(false);
    this.onWindowScroll();

    window.addEventListener('scroll', this.onWindowScroll.bind(this));
  }

  disconnect() {
    window.removeEventListener('scroll', this.onWindowScroll.bind(this));
  }

  setPageNumber(page) {
    this.pageTarget.value = page;
  }

  scrollToResult() {
    jump(this.formTarget, {
      offset: Modernizr.mq('(max-width: 991px)') ? -65 : -180,
      a11y: false,
      duration: 0,
    });
  }

  onFilterChange(resetPageNumber = false) {
    if (resetPageNumber || typeof resetPageNumber !== 'boolean') {
      this.setPageNumber(1);
    }

    this.filterDebounced();
  }

  onConfirmClick() {
    this.closeAllDropdowns();
  }

  onShowResults() {
    this.closeFilter();
  }

  closeFilter() {
    $(this.element.querySelector('#filter')).collapse('hide');
  }

  onWindowClick(event) {
    const { target } = event;
    const sectionElement = target.closest('.custom-journey-filter__column');

    if (!sectionElement) {
      this.closeAllDropdowns();
    }
  }

  closeAllDropdowns() {
    const dropdownNodes = this.element.querySelectorAll('.custom-journey-filter__collapse');
    const dropdowns = Array.prototype.slice.call(dropdownNodes);

    dropdowns.forEach((dropdown) => {
      $(dropdown).collapse('hide');
    });
  }

  onDeleteFilter(e) {
    deleteFilterForButton(e.target.closest('button'));
    this.onFilterChange(true);
  }

  onDeleteFilters() {
    this.selectedDeleteButtonsTargets.forEach(deleteFilterForButton);
    this.onFilterChange(true);
  }

  getSelectedFilters() {
    let selectedValuesFirstForm = [];
    const selectedValuesForm = serializeFormToObject(this.formTarget);

    if (this.hasFirstFormTarget) {
      selectedValuesFirstForm = serializeFormToObject(this.firstFormTarget);
    }

    return [
      ...this.createSelectedFilters(selectedValuesFirstForm),
      ...this.createSelectedFilters(selectedValuesForm),
    ];
  }

  createSelectedFilters(array) {
    const items = [];

    array.forEach((val, key) => {
      if (!val) return;

      let label = getLabelTextForInput(key, val);
      const input = getInputForKeyAndValue(key, val);
      const defaultValue = getDefaultForInput(input);

      if (input.value === defaultValue || !label) return;

      if (input.hasAttribute('data-type') && input.getAttribute('data-type') === 'datepicker') {
        const date = format(new Date(val), 'dd-MM-yyyy');

        label = `Aankomstdatum: ${date}`;
      }

      items.push({
        key,
        value: val,
        label,
      });
    });

    return items;
  }

  outputSelectedFilters(filters) {
    if (!this.hasSelectedTarget) return;

    this.selectedTarget.innerHTML = filters.map(renderSelectedFilter).join('');

    this.selectedTarget.innerHTML += clearFilters();

    this.selectedTarget.style.display = filters.length > 0 ? 'flex' : 'none';
  }

  filterParameters() {
    let parameters = formSerialize(this.formTarget, { hash: true });

    if (this.hasFirstFormTarget) {
      const firstParameters = formSerialize(this.firstFormTarget, { hash: true });
      parameters = { ...firstParameters, ...parameters };
    }

    if (parameters && window.location.search.indexOf('map=1') >= 0) {
      parameters.map = 1;
    }

    return parameters;
  }

  storeFilterParametersInUrl() {
    const parameters = this.filterParameters();

    // Stringify the data object into a query string
    const stringified = qs.stringify(parameters, { encode: false });

    const queryString = stringified !== '' ? `?${stringified}` : '';

    if (hasHistorySupport()) {
      // Get the full base url without any parameters
      const baseUrl = [window.location.protocol, '//', window.location.host, window.location.pathname].join('');

      try {
        // Output the query string after the baseUrl as GET parameters
        window.history.replaceState({}, '', `${baseUrl}${queryString}`);
      } catch (error) {
        // console.error(error);
      }
    }
  }

  onSearchFilter(event) {
    event.preventDefault();

    this.scrollToResults();

    this.filter();
  }

  filter() {
    if (!this.hasFormTarget) return;

    const method = this.formTarget.method.toUpperCase();

    this.emptyResults();
    this.showSpinner(true);
    this.outputSelectedFilters(this.getSelectedFilters());

    const parameters = this.filterParameters();

    // Stringify the data object into a query string
    const stringified = qs.stringify(parameters, { encode: false });

    const queryString = stringified !== '' ? `?${stringified}` : '';

    if (method === 'GET') {
      this.storeFilterParametersInUrl();
    }

    const formAction = this.formTarget.action.split('?')[0];

    $.ajax(`${formAction}${queryString}`, {
      method,
    })
      .then((res) => {
        this.showSpinner(false);

        if (this.hasResultsTarget) {
          this.resultsTarget.innerHTML = res.data;
        }

        if (this.resultsCountTargets.length) {
          this.resultsCountTargets.forEach((target) => {
            target.innerHTML = `${res.count} ${this.resultText(res.count)}`;
            target.style.display = 'inline-block';
          });
        }

        if (res.data_for_map) {
          this.updateMap(res.data_for_map);
        }
      });
  }

  resultText(count) {
    if (count === 1) {
      return this.data.has('result') ? this.data.get('result') : 'resultaat';
    }

    return this.data.has('results') ? this.data.get('results') : 'resultaten';
  }

  emptyResults() {
    if (!this.hasResultsTarget) return;

    this.resultsTarget.innerHTML = '';
  }

  showSpinner(state) {
    if (!this.hasSpinnerTarget) return;

    this.spinnerTarget.style.display = state ? 'block' : 'none';
  }

  updateMap(data) {
    const mapControllerEl = document.querySelector('[data-stimulus-id="map"]');
    const mapController = this.application.getControllerForElementAndIdentifier(mapControllerEl, 'map');

    mapController.updateMap(data);
  }

  onWindowScroll() {
    if (!this.hasSectionTarget) return;

    const top = window.scrollY;

    if (top >= this.sectionTop && !this.isFixed) {
      this.isFixed = true;
      this.setFixedState();
    } else if (top < this.sectionTop && this.isFixed) {
      this.isFixed = false;
      this.setFixedState();
    }
  }

  setFixedState() {
    this.sectionTarget.classList[this.isFixed ? 'add' : 'remove']('is-sticky');
  }

  scrollToResults() {
    const windowBottom = window.scrollY + window.innerHeight;
    const resultsTop = this.resultsTarget.getBoundingClientRect().top + window.scrollY;

    const minimumInScreen = 200;

    const inScreen = (windowBottom - resultsTop) > minimumInScreen;

    if (inScreen) {
      return;
    }

    jump(this.resultsTarget, {
      offset: -100,
    });
  }
}

FilterController.targets = [
  'firstForm',
  'form',
  'page',
  'selected',
  'selectedDeleteButtons',
  'spinner',
  'results',
  'resultsCount',
  'section',
];

export default FilterController;
